import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Card, CardContent } from '@/components/ui/card'
import { SendHorizonal, MessageSquare, MessagesSquare, Star, ArrowRight, CheckCircle2, LayoutList, X, Briefcase, Target, Trophy, Clock, Play, Keyboard, Mic, Video } from 'lucide-react'
import { Button } from '@/components/Common/Button'
import ChatTextarea from '@/components/Common/ChatTextarea'
import { useChatStore, useRoleChatStore } from '@/stores/chatStore'
import { module2Store } from './module2Store'
import { meteringStore } from './meteringStore'
import { observer } from 'mobx-react-lite'
import MainScreen from '../Main/MainScreen'
import { useTranslation } from '@/hooks/useTranslation'
import RolePlaySidebar, { RolePlayMessage, RolePlayMode } from '../Main/RolePlaySidebar'
import ParametersSummaryButton from '@/components/Common/ParametersSummaryButton'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { useStores } from '@/components/StoreProvider'
import { settingsStore } from '@/stores/settingsStore'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useModuleConfig } from './ModuleConfigContext'
import { deriveCardStatus, deriveMasteryPct, displayRolePlayTitle, M2StatusPill, M2CardFoot } from './rolePlayStatus'
import ReviewContent from './ReviewContent'
import CoachMessageCard, { parseCoachMessage, CoachWelcomeSkeleton } from './CoachMessageCard'
import georgiaAvatar from '../../images/georgia.png'

// GraphQL Queries
const GET_PAYLOADS = gql`
  query GetPayloads($projectId: Int!, $page: Int, $limit: Int) {
    getPayloads(projectId: $projectId, page: $page, limit: $limit) {
      data {
        id
        name
        description
      }
    }
  }
`

const GET_ORGANISATION_TARGETS = gql`
  query getOrganisationTargets($organisationId: Int!) {
    getOrganisationTargets(organisationId: $organisationId) {
      id
      moduleId
      payloadId
      payloadName
      coachPayloadId
      elevenlabsVoiceId
      avatarId
      avatar {
        id
        name
        description
        elevenlabsAgentId
        language
      }
      minRating
      minSessions
    }
  }
`

const GET_USER_MODULES = gql`
  query getUserModules {
    getUserModules {
      id
      name
      userCount
      maxUsers
      coachFlowId
      roleplayFlowId
      reviewPromptPayloadId
      timerDuration
      defaultRemainingTime
      reviewPageSize
      metered
      balanceSeconds
    }
  }
`

const GET_PAYLOAD = gql`
  query GetPayload($id: Int!) {
    getPayload(id: $id) {
      id
      name
      content
    }
  }
`

const CHAT_COMPLETION = gql`
  mutation ChatCompletion($messages: [ChatCompletionMessageInput!]!) {
    chatCompletion(messages: $messages) {
      content
    }
  }
`

const SAVE_REVIEW = gql`
  mutation SaveReview($input: SaveReviewInput!) {
    saveReview(input: $input) {
      _id
    }
  }
`

const GET_MICROFRANK_TOKEN = gql`
  query GetMicrofrankToken {
    getMicrofrankToken
  }
`

const GET_COACH_WELCOME = gql`
  query GetCoachWelcome($targetId: Int!) {
    getCoachWelcome(targetId: $targetId) {
      targetId
      welcomeJson
      generatedAt
    }
  }
`

const SAVE_COACH_WELCOME = gql`
  mutation SaveCoachWelcome($targetId: Int!, $welcomeJson: String!) {
    saveCoachWelcome(targetId: $targetId, welcomeJson: $welcomeJson) {
      success
    }
  }
`

const GET_ROLE_PLAY_STATS = gql`
  query GetRolePlayStats {
    getRolePlayStats {
      rolePlayId
      sessionsPlayed
      totalScore
    }
  }
`

const GET_REVIEWS = gql`
  query GetReviews($limit: Int, $offset: Int) {
    getReviews(limit: $limit, offset: $offset) {
      rolePlayId
      score
      mastered
      toImprove
      createdAt
    }
  }
`

interface PayloadData {
  id: number
  name: string
  description?: string
}

interface TargetData {
  id: number
  moduleId: number
  payloadId: number
  payloadName: string
  coachPayloadId: number | null
  elevenlabsVoiceId: string | null
  avatarId: number | null
  avatar: { id: number; name: string | null; description: string | null; elevenlabsAgentId: string | null; language: string | null } | null
  minRating: number
  minSessions: number
}

interface ModuleData {
  id: number
  name: string
  userCount: number
  maxUsers: number
  coachFlowId: number
  roleplayFlowId: number
  reviewPromptPayloadId: number
  timerDuration: number
  defaultRemainingTime: number
  reviewPageSize: number
}

interface RolePlayCard {
  id: number
  targetId: number
  title: string
  description?: string
  moduleId?: number
  coachPayloadId?: number | null
  elevenlabsVoiceId?: string | null
  avatarId?: number
  avatarName?: string
  avatarDescription?: string
  agentId?: string
  language?: string
  // Progress (mock = 0, no API yet)
  sessionsPlayed: number
  targetSessions: number // 0 = don't show session status
  // Scores (mock = 0, no API yet)
  totalScore: number // sum of all scores
  targetScore: number // 0 = don't show score status
}

// Message bubble for chat with markdown rendering
const MessageBubble: React.FC<{ role: string; content: string; isReview?: boolean; isReviewWarning?: boolean; coachWelcome?: boolean }> = ({
  role,
  content,
  isReview,
  isReviewWarning,
  coachWelcome,
}) => {
  const isUser = role === 'user'
  if (!content) return null
  if (role !== 'user' && role !== 'assistant') return null
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-[85%] sm:max-w-[75%] rounded-lg px-3 py-2 sm:px-4 ${
          isUser
            ? 'bg-primary text-white rounded-tr-none'
            : isReviewWarning
              ? 'bg-[#FFE6EC] text-sky-950 border border-[#FEB3C7]'
              : isReview
                ? 'bg-amber-50 text-amber-950 border border-amber-200'
                : coachWelcome
                  ? 'bg-white text-gray-800 border border-gray-200'
                  : 'bg-secondary text-gray-800'
        }`}
      >
        <div className={`prose prose-sm max-w-none dark:prose-invert wrap-break-word ${isUser ? 'text-white' : isReviewWarning ? 'text-sky-950' : isReview ? 'text-amber-950' : 'text-gray-800'}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ hr: (props) => <hr {...props} className="not-prose my-3" /> }}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  )
}

// LLM JSON responses are sometimes wrapped in a ```json ... ``` markdown fence (or have prose around them),
// which makes a naive JSON.parse throw — leaving the raw fenced text to render as a code block and get
// persisted verbatim. Try the raw string, then the fence body, then the first {...} block.
const AVATAR_IMAGE_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4050'

/** Persona avatar on a role play card: the linked Avatar entity's image, or an empty mint circle when the target has none or it fails to load. */
const M2CardAvatar: React.FC<{ avatarId?: number; avatarName?: string }> = ({ avatarId, avatarName }) => {
  const [failed, setFailed] = useState(false)
  const src = avatarId && !failed ? `${AVATAR_IMAGE_BASE}/api/avatars/${avatarId}/image` : null
  return (
    <div className="w-11 h-11 rounded-full overflow-hidden shrink-0" style={{ background: 'var(--m2-mint)' }} title={avatarName} aria-hidden={src ? undefined : true}>
      {src && <img src={src} alt={avatarName ?? ''} className="w-full h-full object-cover block" onError={() => setFailed(true)} />}
    </div>
  )
}

function parseReviewJson(raw: string): Record<string, any> | null {
  const trimmed = raw.trim()
  const candidates: string[] = [trimmed]
  const fenceMatch = trimmed.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/i)
  if (fenceMatch) candidates.push(fenceMatch[1].trim())
  const braceMatch = trimmed.match(/\{[\s\S]*\}/)
  if (braceMatch) candidates.push(braceMatch[0])
  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch {
      // try the next candidate
    }
  }
  return null
}

// --- Module 2 coach screen: right-rail progress + last-attempt cards ---
interface ReviewEntry {
  rolePlayId: number
  score: number
  mastered: string | null
  toImprove: string | null
  createdAt: string
}

interface M2RailData {
  sessions: { played: number; target: number }
  bestScore: number
  masteryPct: number
  lastAttempt: { date: string; score: number; tags: { kind: 'win' | 'fix'; label: string }[] } | null
}

const formatAttemptDate = (isoString: string): string => {
  const date = new Date(isoString)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = date.getHours() >= 12 ? 'PM' : 'AM'
  const hours = date.getHours() % 12 || 12
  return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`
}

// Build the right-rail view-model for the selected role play from its card stats + its reviews (newest-first).
const buildM2RailData = (card: { sessionsPlayed: number; targetSessions: number; totalScore: number; targetScore: number }, reviews: ReviewEntry[]): M2RailData => {
  const bestScore = reviews.reduce((max, r) => Math.max(max, r.score), 0)
  const latest = reviews[0]
  let lastAttempt: M2RailData['lastAttempt'] = null
  if (latest) {
    const tags: { kind: 'win' | 'fix'; label: string }[] = []
    if (latest.mastered) tags.push({ kind: 'win', label: latest.mastered })
    if (latest.toImprove) tags.push({ kind: 'fix', label: latest.toImprove })
    lastAttempt = { date: formatAttemptDate(latest.createdAt), score: latest.score, tags }
  }
  return {
    sessions: { played: card.sessionsPlayed, target: card.targetSessions },
    bestScore,
    masteryPct: deriveMasteryPct(card),
    lastAttempt,
  }
}

const Module2ProgressRail: React.FC<{ data: M2RailData }> = ({ data }) => {
  return (
    <div className="hidden lg:flex w-64 shrink-0 flex-col gap-3.5 p-4 overflow-y-auto" style={{ background: 'var(--m2-bg)' }}>
      {/* YOUR PROGRESS — dark brand card */}
      <div className="rounded-xl p-3.5 text-white shadow-sm" style={{ background: 'var(--m2-brand)' }}>
        <div className="flex items-center gap-1.5 mb-2.5 text-[9.5px] font-bold tracking-[1.5px]" style={{ color: 'var(--m2-lime)' }}>
          <Trophy size={12} />
          YOUR PROGRESS
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2.5">
          <div className="rounded-lg px-2.5 py-2" style={{ background: 'rgba(255,255,255,0.07)' }}>
            <div className="text-[9.5px] uppercase tracking-wide mb-0.5" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Sessions
            </div>
            <div className="text-lg font-bold leading-none flex items-baseline gap-0.5" style={{ color: 'var(--m2-lime)' }}>
              {data.sessions.played}
              <span className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
                / {data.sessions.target}
              </span>
            </div>
          </div>
          <div className="rounded-lg px-2.5 py-2" style={{ background: 'rgba(255,255,255,0.07)' }}>
            <div className="text-[9.5px] uppercase tracking-wide mb-0.5" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Best
            </div>
            <div className="text-lg font-bold leading-none flex items-baseline gap-0.5" style={{ color: 'var(--m2-lime)' }}>
              {data.bestScore.toFixed(1)}
              <span className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
                ★
              </span>
            </div>
          </div>
        </div>
        <div className="flex justify-between text-[10px] mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
          <span>To mastery</span>
          <strong style={{ color: 'var(--m2-lime)' }}>{data.masteryPct}%</strong>
        </div>
        <div className="h-[5px] rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.13)' }}>
          <div className="h-full rounded-full" style={{ width: `${data.masteryPct}%`, background: 'var(--m2-lime)' }} />
        </div>
      </div>

      {/* LAST ATTEMPT */}
      <div className="rounded-xl p-3.5 border shadow-sm" style={{ background: 'var(--m2-card)', borderColor: 'var(--m2-line)' }}>
        <div className="flex items-center gap-1.5 mb-2.5 text-[9.5px] font-bold tracking-[1.5px]" style={{ color: 'var(--m2-muted)' }}>
          <MessageSquare size={12} style={{ color: 'var(--m2-brand)' }} />
          LAST ATTEMPT
        </div>
        {data.lastAttempt ? (
          <>
            <div className="flex items-center justify-between">
              <div className="text-[11px]" style={{ color: 'var(--m2-muted)' }}>
                {data.lastAttempt.date}
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={11} style={{ color: s <= data.lastAttempt!.score ? 'var(--m2-amber)' : '#D8DDD5', fill: s <= data.lastAttempt!.score ? 'var(--m2-amber)' : '#D8DDD5' }} />
                  ))}
                </div>
                <div className="text-[11.5px] font-bold" style={{ color: 'var(--m2-ink)' }}>
                  {data.lastAttempt.score.toFixed(1)}
                </div>
              </div>
            </div>
            {data.lastAttempt.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {data.lastAttempt.tags.map((tag) => (
                  <div
                    key={tag.label}
                    className="text-[9.5px] font-semibold tracking-[0.3px] px-1.5 py-[3px] rounded"
                    style={tag.kind === 'win' ? { background: 'var(--m2-mint)', color: 'var(--m2-brand)' } : { background: 'var(--m2-amber-bg)', color: 'var(--m2-amber-ink)' }}
                  >
                    {tag.label}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-[11px]" style={{ color: 'var(--m2-muted)' }}>
            No attempts yet
          </div>
        )}
      </div>
    </div>
  )
}

// Helper function to calculate status percentage (capped at 100%)
const calculateStatus = (current: number, target: number): number => {
  if (target === 0) return 0
  const percentage = Math.round((current / target) * 100)
  return Math.min(percentage, 100)
}

const StarRating: React.FC<{ rating: number; maxRating?: number }> = ({ rating, maxRating = 5 }) => {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: maxRating }).map((_, index) => (
        <Star key={index} size={12} className={`transition-all duration-200 ${index < rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}`} />
      ))}
    </div>
  )
}

// Progress Bar Component - pill-shaped with blue gradient fill
const ProgressBar: React.FC<{ value?: number }> = ({ value }) => {
  const clampedValue = Math.max(0, Math.min(100, value ?? 0))

  return (
    <div className="h-4 w-20 rounded-full overflow-hidden border-[2px] p-[1.5px] border-[#3D8C71] bg-white">
      <div className="h-full rounded-full transition-all duration-300 ease-out" style={{ width: `${clampedValue}%`, backgroundColor: '#3D8C71' }} />
    </div>
  )
}

const Module2Chat: React.FC = () => {
  const mountedTabRef = useRef(module2Store.activeTab) // capture which tab this instance was created for
  const { t } = useTranslation()
  const { userStore } = useStores()
  const moduleConfig = useModuleConfig()
  const { messages, sendMessage, initSocket, disconnectSocket, isConnected, isLoading, isReady } = useChatStore()
  const [message, setMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const prevSelectedRolePlayRef = useRef<number | null>(null)
  const rolePlayCardsRef = useRef<RolePlayCard[]>([])
  const [hasInitialized, setHasInitialized] = useState(false)
  // Coach welcome cache: when we render a cached welcome we skip the socket entirely until the
  // user actually sends a message (lazy connect). `isCachedWelcome` tracks that pending state.
  const [isCachedWelcome, setIsCachedWelcome] = useState(false)
  const lazyConnectQueryRef = useRef<Record<string, string | number> | null>(null)
  // The cached welcome content, seeded as the coach's prior turn on the first reply so the flow
  // continues the conversation instead of regenerating the welcome (see Module2Chat lazy connect).
  const lazyConnectWelcomeRef = useRef<string | null>(null)
  // On a cache miss we generate the welcome live; this holds the targetId whose welcome we still
  // need to persist once it arrives from the socket.
  const pendingSaveTargetIdRef = useRef<number | null>(null)
  const [selectedMode, setSelectedMode] = useState<RolePlayMode>('text')
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  // Set by the sidebar's "Use voice instead": the sidebar closes on this render and reopens,
  // for the same card, in the new mode on the next one.
  const [reopenSidebarPending, setReopenSidebarPending] = useState(false)
  // How the review names the mode. `camera` is "Video" to the user (the HeyGen `video` mode is not reachable here).
  const reviewModeLabel = selectedMode === 'camera' ? 'Video' : selectedMode.charAt(0).toUpperCase() + selectedMode.slice(1)
  const [isCardsPanelOpen, setIsCardsPanelOpen] = useState(false)
  const [reviewSystemPrompt, setReviewSystemPrompt] = useState<string | null>(null)
  /** Coach payload: direct getPayload(id) — only the instruction, no agent-added content. Used for ParametersSummaryButton and sidebar (chat receives payload that went through the agent). */
  const [selectedPayloadContent, setSelectedPayloadContent] = useState<string | null>(null)
  const [isGeneratingReview, setIsGeneratingReview] = useState(false)
  // The coach chat is interactive either when the socket is ready, or when we are showing a cached
  // welcome (lazy-connect mode) — in the latter case there is no socket yet but the user can read
  // and type to trigger the connection.
  const coachReady = isReady || isCachedWelcome

  // When the browser tab was in background for a long time, the WebSocket dies and Socket.IO's
  // limited reconnection attempts (5) are exhausted before the user returns.
  // On tab focus, check if socket is dead and force a full reinit.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return
      if (module2Store.mode !== 'chat') return
      const { isReady, isConnected } = useChatStore.getState()
      if (isReady && isConnected) return
      disconnectSocket()
      setHasInitialized(false)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [disconnectSocket])

  // API data state - Modules
  const [salesModules, setSalesModules] = useState<ModuleData[]>([])
  const [activeModuleId, setActiveModuleId] = useState<number | null>(null)
  const [isLoadingModules, setIsLoadingModules] = useState(true)

  // API data state - Cards
  const [rolePlayCards, setRolePlayCards] = useState<RolePlayCard[]>([])
  const [isLoadingCards, setIsLoadingCards] = useState(true)
  const [cardsError, setCardsError] = useState<string | null>(null)
  const [isRefreshingUser, setIsRefreshingUser] = useState(true)

  // Reviews — feed the right-rail progress + last-attempt cards (filtered per selected role play)
  const [reviews, setReviews] = useState<ReviewEntry[]>([])
  const fetchReviews = useCallback(async () => {
    try {
      const response = await authorizedGraphQLRequest<{ getReviews: ReviewEntry[] }>(GET_REVIEWS, { limit: 0, offset: 0 })
      setReviews(response.getReviews || [])
    } catch (err) {
      console.error('Failed to fetch reviews:', err)
    }
  }, [])
  useEffect(() => {
    fetchReviews()
  }, [fetchReviews])

  // Refresh user profile then fetch modules/cards.
  // Single effect to guarantee sequential execution: refresh completes before
  // we check organization_id. Cannot use checkAuth() because it toggles
  // userStore.isLoading, which causes OrganizationBasedRoute to unmount/remount
  // Module2Page in a loop.
  useEffect(() => {
    const fetchData = async () => {
      // 1. Silently refresh user profile to pick up admin-side org/module changes
      setIsRefreshingUser(true)
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          const response = await axios.get(`${import.meta.env.VITE_API_URL}/me`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (response.data.user) {
            userStore.setUser(response.data.user)
          }
        }
      } catch {
        // Proceed with existing (possibly stale) user data
      } finally {
        setIsRefreshingUser(false)
      }

      // 2. Fetch modules and cards with the (now fresh) organization_id
      if (!userStore.user?.organization_id) {
        setIsLoadingModules(false)
        setIsLoadingCards(false)
        return
      }

      try {
        setIsLoadingModules(true)
        setIsLoadingCards(true)
        setCardsError(null)

        const orgId = userStore.user.organization_id

        const modulesResponse = await authorizedGraphQLRequest<{
          getUserModules: ModuleData[]
        }>(GET_USER_MODULES)

        const modules = modulesResponse.getUserModules || []
        setSalesModules(modules)

        // Sync modules to store for sidebar
        module2Store.setModules(modules)

        // Set first module as active if not set
        if (modules.length > 0 && module2Store.activeModuleId === null) {
          setActiveModuleId(modules[0].id)
          module2Store.setActiveModuleId(modules[0].id)
        }

        // Auto-save first module as preference if none saved
        if (modules.length > 0 && settingsStore.module === null) {
          settingsStore.updateSettings({ module: modules[0].id })
        }

        // Fetch targets and role play stats in parallel
        const [targetsResponse, statsResponse] = await Promise.all([
          authorizedGraphQLRequest<{ getOrganisationTargets: TargetData[] }>(GET_ORGANISATION_TARGETS, { organisationId: orgId }),
          authorizedGraphQLRequest<{ getRolePlayStats: { rolePlayId: number; sessionsPlayed: number; totalScore: number }[] }>(GET_ROLE_PLAY_STATS),
        ])

        const targets = targetsResponse.getOrganisationTargets || []
        const stats = statsResponse.getRolePlayStats || []

        // Build a lookup map for stats by rolePlayId
        const statsMap = new Map(stats.map((s) => [s.rolePlayId, s]))

        // Create cards from targets, merging real stats
        const cards: RolePlayCard[] = targets.map((target) => {
          const stat = statsMap.get(target.payloadId)
          return {
            id: target.payloadId,
            targetId: target.id,
            title: target.payloadName,
            description: undefined,
            moduleId: target.moduleId,
            coachPayloadId: target.coachPayloadId,
            elevenlabsVoiceId: target.elevenlabsVoiceId,
            avatarId: target.avatar?.id ?? undefined,
            avatarName: target.avatar?.name ?? undefined,
            avatarDescription: target.avatar?.description ?? undefined,
            agentId: target.avatar?.elevenlabsAgentId ?? undefined,
            language: target.avatar?.language ?? undefined,
            sessionsPlayed: stat?.sessionsPlayed || 0,
            targetSessions: target.minSessions || 0,
            totalScore: stat?.totalScore || 0,
            targetScore: target.minRating || 0,
          }
        })

        setRolePlayCards(cards)
        rolePlayCardsRef.current = cards

        // Default to first module's Role Play tab with first card selected (so user lands in working scenario)
        if (module2Store.activeTab === 'home' && modules.length > 0 && cards.length > 0) {
          const firstModuleId = modules[0].id
          const firstCard = cards.find((c) => c.moduleId === firstModuleId)
          if (firstCard) {
            module2Store.setActiveTab(`chat-${firstModuleId}`)
            module2Store.setActiveModuleId(firstModuleId)
            setActiveModuleId(firstModuleId)
            module2Store.setMode('chat')
            module2Store.setSelectedRolePlay(firstCard.id)
          }
        }

        // Auto-select first card when switching to a module with no saved selection
        if (module2Store.selectedRolePlay === null && module2Store.activeModuleId != null && module2Store.mode === 'chat' && cards.length > 0) {
          const firstCard = cards.find((c) => c.moduleId === module2Store.activeModuleId)
          if (firstCard) {
            module2Store.setSelectedRolePlay(firstCard.id)
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err)
        setCardsError('Failed to load data')
      } finally {
        setIsLoadingModules(false)
        setIsLoadingCards(false)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on mount; refreshes user then fetches data sequentially
  }, [])

  // Fetch review system prompt eagerly on mount
  useEffect(() => {
    const fetchReviewPrompt = async () => {
      try {
        const response = await authorizedGraphQLRequest<{ getPayload: { id: number; name: string; content: string } }>(GET_PAYLOAD, { id: moduleConfig.reviewPromptPayloadId })
        if (response.getPayload?.content) {
          setReviewSystemPrompt(response.getPayload.content)
        }
      } catch (err) {
        console.error('Failed to fetch review system prompt:', err)
      }
    }
    fetchReviewPrompt()
  }, [moduleConfig.reviewPromptPayloadId])

  // Sync local activeModuleId with store when navigating via sidebar
  useEffect(() => {
    if (module2Store.activeModuleId !== null && module2Store.activeModuleId !== activeModuleId) {
      setActiveModuleId(module2Store.activeModuleId)
    }
  }, [module2Store.activeModuleId])

  // Fetch coach payload directly (clean instruction only; chat gets payload that passed through the agent)
  useEffect(() => {
    const payloadId = module2Store.selectedRolePlay
    if (payloadId == null) {
      setSelectedPayloadContent(null)
      return
    }
    // Find the selected card's coachPayloadId
    const card = rolePlayCards.find((c) => c.id === payloadId)
    const coachId = card?.coachPayloadId
    if (coachId == null) {
      setSelectedPayloadContent(null)
      return
    }
    let cancelled = false
    const fetchPayload = async () => {
      try {
        const response = await authorizedGraphQLRequest<{ getPayload: { id: number; name: string; content: string } }>(GET_PAYLOAD, { id: coachId })
        if (!cancelled && response.getPayload?.content) {
          setSelectedPayloadContent(response.getPayload.content)
        } else if (!cancelled) {
          setSelectedPayloadContent(null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch coach payload:', err)
          setSelectedPayloadContent(null)
        }
      }
    }
    fetchPayload()
    return () => {
      cancelled = true
    }
  }, [module2Store.selectedRolePlay, rolePlayCards])

  // Fetch roleplay payload content (for ElevenLabs voice prompt override)
  const [roleplayPayloadContent, setRoleplayPayloadContent] = useState<string | null>(null)
  useEffect(() => {
    const payloadId = module2Store.selectedRolePlay
    if (payloadId == null) {
      setRoleplayPayloadContent(null)
      return
    }
    let cancelled = false
    const fetchPayload = async () => {
      try {
        const response = await authorizedGraphQLRequest<{ getPayload: { id: number; name: string; content: string } }>(GET_PAYLOAD, { id: payloadId })
        if (!cancelled && response.getPayload?.content) {
          setRoleplayPayloadContent(response.getPayload.content)
        } else if (!cancelled) {
          setRoleplayPayloadContent(null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch roleplay payload:', err)
          setRoleplayPayloadContent(null)
        }
      }
    }
    fetchPayload()
    return () => {
      cancelled = true
    }
  }, [module2Store.selectedRolePlay])

  // Filter cards by active module - use store directly for MobX reactivity
  const filteredCards = module2Store.activeModuleId ? rolePlayCards.filter((card) => card.moduleId === module2Store.activeModuleId) : rolePlayCards

  const tabClasses = (isActive: boolean) => `
    px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 text-xs sm:text-sm font-medium rounded-full transition-all duration-200 touch-manipulation
    ${isActive ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'}
  `

  const handleSelectRolePlay = (rolePlayNumber: number) => {
    module2Store.setSelectedRolePlay(rolePlayNumber)
  }

  const handleGoToChat = (cardId: number) => {
    const card = rolePlayCards.find((c) => c.id === cardId)
    if (card?.moduleId) {
      module2Store.setActiveModuleId(card.moduleId)
      setActiveModuleId(card.moduleId)
      // Set dynamic tab for this module's chat
      module2Store.setActiveTab(`chat-${card.moduleId}`)
    }
    module2Store.setSelectedRolePlay(cardId)
    module2Store.setMode('chat')
  }

  const handleStartChat = () => {
    // Use current active module for dynamic tab
    if (module2Store.activeModuleId) {
      module2Store.setActiveTab(`chat-${module2Store.activeModuleId}`)
    } else {
      module2Store.setActiveTab('chat')
    }
    module2Store.setMode('chat')
  }

  const handleStartCall = () => {
    module2Store.setMode('voice')
  }

  const handleReturnToSelection = () => {
    disconnectSocket()
    setHasInitialized(false)
    module2Store.setActiveTab('home')
    module2Store.setMode('selection')
  }

  const handleRolePlayEnd = async (rolePlayMessages: RolePlayMessage[]) => {
    const MIN_REVIEW_DURATION_MS = 30_000
    const NOT_ENOUGH_DATA_MESSAGE = 'You quit role play unexpectedly. There is not enough data to review.'
    const rolePlaySessionToken = useRoleChatStore.getState().sessionToken
    setIsSidebarOpen(false)

    if (rolePlayMessages.length === 0) return

    setIsGeneratingReview(true)

    const meaningfulUserMessages = rolePlayMessages.filter((msg) => msg.role === 'user' && msg.content.trim().length > 0)
    const messageTimestamps = rolePlayMessages.map((msg) => msg.timestamp).filter((ts) => Number.isFinite(ts))
    const sessionDurationMs = messageTimestamps.length > 1 ? Math.max(...messageTimestamps) - Math.min(...messageTimestamps) : 0
    const hasEnoughDataForReview = meaningfulUserMessages.length > 0 && sessionDurationMs >= MIN_REVIEW_DURATION_MS

    if (!hasEnoughDataForReview) {
      const notEnoughDataMessage = {
        id: Date.now().toString(),
        content: NOT_ENOUGH_DATA_MESSAGE,
        role: 'ai' as const,
        timestamp: new Date(),
        payload: { isReviewWarning: true },
      }
      useChatStore.setState((state) => ({
        messages: [...state.messages, notEnoughDataMessage],
      }))
      setIsGeneratingReview(false)
      return
    }

    // Show loading indicator in the coach chat while generating review
    useChatStore.setState({ isLoading: true })

    try {
      // Get first coach message (contains role play instructions)
      const coachMessages = useChatStore.getState().messages
      const firstCoachMessage = coachMessages.find((m) => m.role === 'ai' || m.role === 'assistant')

      // Format all role play messages as conversation text
      const conversationText = rolePlayMessages.map((msg) => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`).join('\n\n')

      // Build single system message: review prompt + coach instructions + conversation
      const systemContent = [reviewSystemPrompt, firstCoachMessage ? `\n\n${firstCoachMessage.content}` : '', `\n\nConversation:\n${conversationText}`].filter(Boolean).join('')

      const messages = [{ role: 'system', content: systemContent }]

      const response = await authorizedGraphQLRequest<{ chatCompletion: { content: string } }>(CHAT_COMPLETION, { messages })
      const rawContent = response.chatCompletion?.content

      if (rawContent) {
        let content = rawContent
        let payload: Record<string, any> | undefined
        let score = 0
        let mastered = ''
        let toImprove = ''

        const parsed = parseReviewJson(rawContent)
        if (parsed) {
          if (typeof parsed.reviewMessage === 'string' && typeof parsed.score === 'number') {
            content = parsed.reviewMessage
            score = Math.round(Math.max(0, Math.min(5, parsed.score)))
            payload = { isReview: true, score }
          }
          if (typeof parsed.mastered === 'string') mastered = parsed.mastered
          if (typeof parsed.toImprove === 'string') toImprove = parsed.toImprove
          // Carry highlights + mode onto the review message so it can render the shared ReviewContent design
          if (payload) {
            payload.mastered = mastered
            payload.toImprove = toImprove
            payload.mode = reviewModeLabel
          }
        }

        const reviewMessage = {
          id: Date.now().toString(),
          content,
          role: 'ai' as const,
          timestamp: new Date(),
          payload,
        }
        useChatStore.setState((state) => ({
          messages: [...state.messages, reviewMessage],
          isLoading: false,
        }))
        // Save review to database and update card stats locally (sessionToken links to Chat History)
        const rolePlayId = module2Store.selectedRolePlay
        const rolePlayName = selectedRolePlay?.title || 'Unknown'
        let sessionTokenForSave: string | null = rolePlaySessionToken
        if (!sessionTokenForSave) {
          try {
            const { getMicrofrankToken } = await authorizedGraphQLRequest<{ getMicrofrankToken: string }>(GET_MICROFRANK_TOKEN)
            sessionTokenForSave = getMicrofrankToken || null
          } catch (e) {
            console.error('[Module2Chat] Failed to get session token for review:', e)
          }
        }
        authorizedGraphQLRequest(SAVE_REVIEW, {
          input: {
            rolePlayName,
            rolePlayId: rolePlayId || 0,
            score,
            mode: reviewModeLabel,
            feedback: content,
            mastered,
            toImprove,
            sessionToken: sessionTokenForSave,
          },
        })
          .then(() => {
            // Update card stats locally so UI reflects the new session immediately
            if (rolePlayId) {
              setRolePlayCards((prev) => prev.map((card) => (card.id === rolePlayId ? { ...card, sessionsPlayed: card.sessionsPlayed + 1, totalScore: card.totalScore + score } : card)))
            }
            // Refetch reviews so the progress rail reflects this attempt (server-canonical date/score/highlights)
            fetchReviews()
          })
          .catch((err) => console.error('Failed to save review:', err))
      } else {
        useChatStore.setState({ isLoading: false })
      }
    } catch (err) {
      console.error('Failed to get review from OpenAI:', err)
      const errorMessage = {
        id: Date.now().toString(),
        content: 'Failed to generate review. Please try again.',
        role: 'ai' as const,
        timestamp: new Date(),
      }
      useChatStore.setState((state) => ({
        messages: [...state.messages, errorMessage],
        isLoading: false,
      }))
    } finally {
      setIsGeneratingReview(false)
    }
  }

  // Reopen the sidebar after a mode switch (see onSwitchMode): the close has been rendered, so the
  // sidebar has reset and will initialise afresh in the new mode.
  useEffect(() => {
    if (reopenSidebarPending && !isSidebarOpen) {
      setReopenSidebarPending(false)
      setIsSidebarOpen(true)
    }
  }, [reopenSidebarPending, isSidebarOpen])

  // When user switches Role Play card in chat mode, reconnect with new rolePlayNumber
  useEffect(() => {
    if (module2Store.mode !== 'chat') return
    const current = module2Store.selectedRolePlay
    const prev = prevSelectedRolePlayRef.current
    prevSelectedRolePlayRef.current = current
    if (!hasInitialized || prev === null) return
    if (prev === current) return
    disconnectSocket()
    setIsCachedWelcome(false)
    lazyConnectQueryRef.current = null
    lazyConnectWelcomeRef.current = null
    pendingSaveTargetIdRef.current = null
    setHasInitialized(false)
  }, [module2Store.mode, module2Store.selectedRolePlay, hasInitialized, disconnectSocket])

  // Auto-connect when in chat mode. Wait for cards to finish loading so coachPayloadId is available.
  useEffect(() => {
    const mountedOnChatTab = mountedTabRef.current.startsWith('chat-')
    if (module2Store.mode === 'chat' && !hasInitialized && mountedOnChatTab && module2Store.modules.length > 0 && module2Store.selectedRolePlay != null && !isLoadingCards) {
      const initializeChat = async () => {
        const selectedCard = rolePlayCardsRef.current.find((c) => c.id === module2Store.selectedRolePlay)
        const payloadId = selectedCard?.coachPayloadId ?? module2Store.selectedRolePlay ?? undefined
        const query: Record<string, string | number> = {
          flowId: moduleConfig.coachFlowId,
          initialState: JSON.stringify({
            rolePlayNumber: module2Store.selectedRolePlay,
            payloadId, // coach payload id from target; falls back to roleplay payload id if not set
          }),
        }

        // Read-through cache: try the shared cached welcome first so browsing is instant and free.
        const targetId = selectedCard?.targetId
        if (targetId != null) {
          try {
            const { getCoachWelcome } = await authorizedGraphQLRequest<{ getCoachWelcome: { welcomeJson: string } | null }>(GET_COACH_WELCOME, { targetId })
            if (getCoachWelcome?.welcomeJson) {
              // Cache hit: render the welcome with no socket; defer connecting until the first reply.
              useChatStore.setState({
                messages: [
                  {
                    id: Date.now().toString(),
                    content: getCoachWelcome.welcomeJson,
                    role: 'ai',
                    timestamp: new Date(),
                    payload: { coachWelcomeCached: true },
                  },
                ],
                isLoading: false,
              })
              lazyConnectQueryRef.current = query
              lazyConnectWelcomeRef.current = getCoachWelcome.welcomeJson
              setIsCachedWelcome(true)
              return
            }
          } catch (e) {
            console.error('[Module2Chat] getCoachWelcome failed, falling back to live generation:', e)
          }
        }

        // Cache miss (or no targetId): generate live as before, and remember to persist the result.
        pendingSaveTargetIdRef.current = targetId ?? null
        await initSocket(query, async () => {
          await sendMessage('', 'system')
        })
      }

      initializeChat()
      setHasInitialized(true)
    }

    return () => {
      if (module2Store.mode !== 'chat') {
        setHasInitialized(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initSocket/sendMessage refs may change; only depend on mode, hasInitialized, selectedRolePlay, and isLoadingCards
  }, [module2Store.mode, hasInitialized, module2Store.selectedRolePlay, isLoadingCards])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Persist a freshly generated welcome (cache miss) so the next visitor gets an instant cache hit.
  useEffect(() => {
    const targetId = pendingSaveTargetIdRef.current
    if (targetId == null) return
    const welcome = messages.find((m) => (m.role === 'ai' || m.role === 'assistant') && parseCoachMessage(m.content))
    if (!welcome) return
    pendingSaveTargetIdRef.current = null
    authorizedGraphQLRequest(SAVE_COACH_WELCOME, { targetId, welcomeJson: welcome.content }).catch((err) => console.error('[Module2Chat] saveCoachWelcome failed:', err))
  }, [messages])

  // Exit cached-welcome mode only once the socket is fully READY (the backend 'ready' event), not on
  // bare 'connect' — isConnected flips before isReady, and clearing too early would drop coachReady to
  // false in that gap, disabling the input mid-transition.
  useEffect(() => {
    if (isReady && isCachedWelcome) setIsCachedWelcome(false)
  }, [isReady, isCachedWelcome])

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    // Lazy connect: the first reply after a cached welcome. Open the socket and send the user's
    // message with the cached welcome seeded as the coach's prior turn (extraMessages). Microfrank
    // injects that assistant message into the fold's history, so the coach flow sees an ongoing
    // conversation and replies normally instead of regenerating the welcome — one LLM call, no
    // duplicate, and the reply is coherent with the welcome the user actually read.
    if (isCachedWelcome && !isConnected) {
      const query = lazyConnectQueryRef.current
      if (!query) return
      const userText = message
      const seededWelcome = lazyConnectWelcomeRef.current
      const extraMessages = seededWelcome ? [{ id: `seed-${Date.now()}`, content: seededWelcome, role: 'assistant' as const, timestamp: new Date() }] : undefined
      setMessage('')
      // Keep isCachedWelcome true through the connect so coachReady stays true (input enabled) until the
      // socket is ready. Optimistically show the user's message + a typing bubble for a seamless transition.
      useChatStore.setState((s) => ({ messages: [...s.messages, { id: `u-${Date.now()}`, content: userText, role: 'user', timestamp: new Date() }], isLoading: true }))
      await initSocket(query, async () => {
        await sendMessage(userText, 'user', undefined, extraMessages, { skipLocalEcho: true })
      })
      return
    }

    if (!isConnected) return
    await sendMessage(message)
    setMessage('')
  }

  const selectedRolePlay = rolePlayCards.find((card) => card.id === module2Store.selectedRolePlay)

  // Selection screen
  if (module2Store.mode === 'selection') {
    // No modules assigned — show dedicated empty state (no microfrank init)
    const doneLoading = !isRefreshingUser && !isLoadingModules && !isLoadingCards
    if (doneLoading && salesModules.length === 0) {
      return (
        <div className="bg-card text-card-foreground rounded-xl flex flex-col h-full md:flex-1 md:max-h-full min-h-0 border-0 shadow-xl overflow-hidden">
          <div className="flex flex-col items-center justify-center flex-1 gap-4 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Briefcase className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">No modules connected</h2>
            <p className="text-gray-500 text-sm max-w-sm">You are not connected to any modules yet. Please contact your administrator to get access.</p>
          </div>
        </div>
      )
    }

    return (
      <div className="bg-card text-card-foreground rounded-xl flex flex-col h-full md:flex-1 md:max-h-full min-h-0 border-0 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="text-center pt-4 pb-3 px-3 sm:pt-6 sm:pb-4 sm:px-4 md:px-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-primary tracking-tight">{t('main.home.title')}</h1>
          <p className="text-gray-400 text-xs sm:text-sm mt-1.5 sm:mt-2 truncate max-w-[95vw] mx-auto">
            {t('main.home.selected')}: <span className="text-primary font-medium">{selectedRolePlay ? displayRolePlayTitle(selectedRolePlay.title) : 'None'}</span>
          </p>
        </div>

        {/* Module Tabs - Dynamic from API */}
        <div className="flex justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 flex-wrap">
          {isRefreshingUser || isLoadingModules ? (
            <div className="flex gap-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="animate-pulse h-9 w-24 bg-gray-200 rounded-full" />
              ))}
            </div>
          ) : (
            salesModules.map((mod) => (
              <button
                key={mod.id}
                onClick={() => {
                  setActiveModuleId(mod.id)
                  module2Store.setActiveModuleId(mod.id)
                }}
                className={tabClasses(module2Store.activeModuleId === mod.id)}
              >
                {mod.name}
              </button>
            ))
          )}
        </div>

        {/* Cards Grid */}
        <div className="flex-1 px-3 sm:px-4 md:px-6 lg:px-8 pb-3 sm:pb-4 pt-4 sm:pt-6 overflow-y-auto min-h-0">
          {isRefreshingUser || isLoadingCards ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mx-auto max-w-6xl">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-xl sm:rounded-2xl p-3 sm:p-4 flex flex-col border border-gray-200 bg-white">
                  <div className="flex-1 min-w-0">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="h-3 bg-gray-200 rounded w-16" />
                        <div className="h-3 bg-gray-200 rounded w-8" />
                        <div className="h-4 bg-gray-200 rounded-full w-20" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2 mt-3 border-t border-gray-100">
                    <div className="h-3.5 bg-gray-200 rounded w-8" />
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <div key={j} className="h-3 w-3 bg-gray-200 rounded-sm" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : cardsError ? (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-red-500">{cardsError}</p>
              <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
                Retry
              </button>
            </div>
          ) : filteredCards.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                <MessageSquare className="h-7 w-7 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700">No role plays yet</h3>
              <p className="text-gray-500 text-sm max-w-sm">There are no role plays configured for this module yet. Please contact your administrator to set them up.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mx-auto max-w-6xl">
              {filteredCards.map((card) => {
                const isSelected = module2Store.selectedRolePlay === card.id
                const averageScore = card.sessionsPlayed > 0 ? Math.round(card.totalScore / card.sessionsPlayed) : 0
                const sessionStatus = calculateStatus(card.sessionsPlayed, card.targetSessions)
                const scoreStatus = calculateStatus(averageScore, card.targetScore > 0 ? card.targetScore / (card.targetSessions || 1) : 0)

                return (
                  <div
                    key={card.id}
                    onClick={() => handleSelectRolePlay(card.id)}
                    className={`
                    group relative cursor-pointer rounded-xl sm:rounded-2xl p-3 sm:p-4 flex flex-col
                    transition-all duration-200 ease-out border
                    hover:-translate-y-0.5 sm:hover:-translate-y-1 hover:shadow-md active:scale-[0.99]
                    ${isSelected ? 'border-primary bg-primary/[0.03]' : 'border-gray-200 bg-white hover:bg-gray-50/50 hover:border-gray-300'}
                  `}
                  >
                    {/* Go to Chat Icon */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleGoToChat(card.id)
                      }}
                      className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 p-2 sm:p-1.5 rounded-lg bg-gray-100 hover:bg-primary hover:text-white text-gray-500 transition-all duration-200 opacity-0 group-hover:opacity-100 touch-manipulation"
                      title="Go to Chat"
                    >
                      <ArrowRight size={14} />
                    </button>

                    {/* Card Content */}
                    <div className="flex-1 min-w-0">
                      {/* Card Title */}
                      <h3
                        className={`
                      font-medium text-[13px] sm:text-[14px] mb-2 leading-snug transition-colors duration-200 pr-8 truncate
                      ${isSelected ? 'text-primary' : 'text-gray-700 group-hover:text-gray-900'}
                    `}
                      >
                        {displayRolePlayTitle(card.title)}
                      </h3>
                      {/* Progress Section */}

                      {/* Debug Card Data */}
                      {/* <div className="mb-2 p-2 bg-gray-100 rounded text-xs font-mono ">
                      <pre>{JSON.stringify(card, null, 2)}</pre>
                    </div>  */}

                      <div className="space-y-2">
                        {/* Sessions */}
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Sessions</span>
                          <span className="font-medium text-gray-700">
                            {card.sessionsPlayed}/{card.targetSessions || '∞'}
                          </span>
                          <ProgressBar value={sessionStatus} />
                        </div>

                        {/* Average Rating with Stars */}
                        {/* <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Avg Score</span>
                        {card.sessionsPlayed > 0 ? (
                          <StarRating rating={Math.round(averageScore / 20)} />
                        ) : (
                          <span className="font-medium text-gray-400">-</span>
                        )}
                      </div> */}

                        {/* Progress Bar */}
                        <div className="mt-2"></div>
                      </div>
                    </div>

                    {/* Status Section - pinned to bottom */}
                    <div className="flex items-center gap-3 pt-2 mt-3 border-t border-gray-100">
                      {/* Session Status */}
                      {card.targetSessions > 0 && (
                        <div className="flex items-center gap-1.5" title={`Session: ${sessionStatus}%`}>
                          <CheckCircle2 size={14} className={sessionStatus >= 100 ? 'text-green-500' : 'text-gray-400'} />
                          <span className={`text-xs font-medium ${sessionStatus >= 100 ? 'text-green-600' : 'text-gray-500'}`}>{sessionStatus}%</span>
                        </div>
                      )}

                      {/* Score Status with Stars - always show 5 stars */}
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} size={12} className={`${card.sessionsPlayed > 0 && star <= averageScore ? 'fill-[#3D8C71] text-[#3D8C71]' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Start Chat Button */}
        <div className="flex justify-center py-4 sm:py-5 px-3 sm:px-4 border-t border-gray-100/80 shrink-0">
          <Button variant="outline" onClick={handleStartChat} leftIcon={<MessageSquare size={18} />} className="w-full sm:w-auto min-h-11 touch-manipulation">
            {t('main.home.start_chat')}
          </Button>
        </div>
      </div>
    )
  }

  // Chat mode
  if (module2Store.mode === 'chat') {
    // Convert rolePlayCard to the format RolePlaySidebar expects
    const sidebarCard = selectedRolePlay
      ? {
          id: String(selectedRolePlay.id),
          targetId: selectedRolePlay.targetId,
          title: displayRolePlayTitle(selectedRolePlay.title),
          playedTimes: selectedRolePlay.sessionsPlayed,
          rating: selectedRolePlay.sessionsPlayed > 0 ? Math.round(selectedRolePlay.totalScore / selectedRolePlay.sessionsPlayed / 20) : 0, // Convert % to 1-5 stars
          instructions: roleplayPayloadContent ?? selectedPayloadContent ?? selectedRolePlay.description, // roleplay payload → coach payload → description
          avatarId: selectedRolePlay.avatarId ?? undefined,
          avatarName: selectedRolePlay.avatarName ?? undefined,
          avatarDescription: selectedRolePlay.avatarDescription ?? undefined,
          agentId: selectedRolePlay.agentId ?? undefined,
          language: selectedRolePlay.language ?? undefined,
        }
      : null

    // Objectives come from the live coach welcome message (the same server JSON the coach card renders),
    // so the role play voice screen shows exactly the same objectives as the coach welcome chat.
    const coachWelcomeMessage = messages.find((m) => (m.role === 'assistant' || m.role === 'ai') && parseCoachMessage(m.content))
    const coachObjectives = coachWelcomeMessage ? parseCoachMessage(coachWelcomeMessage.content)?.objectives : undefined

    // Allow switching cards once the coach welcome is shown — either a live response, or a cached
    // welcome rendered without a socket (coachReady covers the lazy-connect case).
    const hasReceivedChatResponse = coachReady && messages.some((m) => m.role === 'assistant' || m.role === 'ai')

    // Shared cards list content for sidebar (lg+) and drawer (mobile/tablet)
    const cardsListContent = (
      <>
        <div className="flex items-center justify-between px-5 pt-[18px] pb-3 shrink-0">
          <span className="text-[10.5px] font-bold tracking-[1.4px] uppercase" style={{ color: 'var(--m2-muted)' }}>
            Role plays
          </span>
          {!isLoadingCards && filteredCards.length > 0 && (
            <span className="text-[11.5px] font-medium" style={{ color: 'var(--m2-muted)' }}>
              {filteredCards.length} total
            </span>
          )}
        </div>
        {isLoadingCards ? (
          <div className="p-4 text-gray-400 text-sm">Loading...</div>
        ) : filteredCards.length === 0 ? (
          <div className="p-4 text-gray-500 text-sm text-center">No role plays configured for this module yet. Contact your administrator.</div>
        ) : (
          <div className="flex flex-col gap-2.5 px-4 pb-4 overflow-y-auto min-h-0">
            {filteredCards.map((card) => {
              const isSelected = module2Store.selectedRolePlay === card.id
              const isCardDisabled = isGeneratingReview || (!hasReceivedChatResponse && !isSelected)
              const averageStars = card.sessionsPlayed > 0 ? card.totalScore / card.sessionsPlayed : 0
              const status = deriveCardStatus(card)
              return (
                <div
                  key={card.id}
                  role="button"
                  tabIndex={isCardDisabled ? -1 : 0}
                  onClick={
                    isCardDisabled
                      ? undefined
                      : () => {
                          handleSelectRolePlay(card.id)
                          setIsCardsPanelOpen(false)
                        }
                  }
                  onKeyDown={(e) => {
                    if (!isCardDisabled && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      handleSelectRolePlay(card.id)
                      setIsCardsPanelOpen(false)
                    }
                  }}
                  className={`
                    relative cursor-pointer rounded-[11px] px-3.5 py-3 flex flex-col bg-white border
                    transition-all duration-150 ease-out
                    ${isSelected ? 'border-[var(--m2-brand)] shadow-[0_0_0_2px_rgba(15,76,58,0.08)]' : 'border-[var(--m2-line)] hover:border-[var(--m2-mint-deep)] hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(15,76,58,0.05)]'}
                    ${isCardDisabled ? 'opacity-60 cursor-not-allowed pointer-events-none' : ''}
                  `}
                >
                  <div className="flex items-center justify-end gap-2 mb-2">
                    <M2StatusPill status={status} />
                  </div>

                  <div className="flex items-center gap-[11px] mb-2">
                    <M2CardAvatar key={card.avatarId ?? 'none'} avatarId={card.avatarId} avatarName={card.avatarName} />
                    <h3 className="font-bold text-[13.5px] leading-[1.3] tracking-[-0.2px] min-w-0" style={{ color: 'var(--m2-ink)' }}>
                      {displayRolePlayTitle(card.title)}
                    </h3>
                  </div>

                  <M2CardFoot sessionsPlayed={card.sessionsPlayed} targetSessions={card.targetSessions} avgStars={averageStars} />
                </div>
              )
            })}
          </div>
        )}
      </>
    )

    return (
      <>
        <div className="module2-coach flex flex-1 min-h-0 w-full min-w-0" style={{ background: 'var(--m2-bg)' }}>
          {/* Left: Role Play cards (targets) — desktop only */}
          <div className="hidden lg:flex w-72 shrink-0 relative flex-col h-full border-r overflow-hidden" style={{ background: 'var(--m2-cards-col)', borderColor: 'var(--m2-line)' }}>
            {cardsListContent}
          </div>

          {/* Center: Chat */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {/* Content header — breadcrumb + hero (template ".content-header"), desktop only */}
            <div className="hidden lg:block shrink-0 px-6 pt-5 pb-2">
              <div className="flex items-center gap-1.5 text-[11.5px] mb-2">
                <span style={{ color: 'var(--m2-muted)' }}>{moduleConfig.name || 'Module'}</span>
                <span style={{ color: '#C9D2CB' }}>/</span>
                <span className="font-medium" style={{ color: 'var(--m2-ink)' }}>
                  Role play
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3.5 min-w-0">
                  <div
                    className="w-12 h-12 rounded-xl shrink-0 flex items-center justify-center border"
                    style={{ background: 'var(--m2-mint)', borderColor: 'var(--m2-mint-deep)', color: 'var(--m2-brand)' }}
                    aria-hidden="true"
                  >
                    <MessagesSquare size={26} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[19px] font-bold leading-tight tracking-[-0.4px] truncate" style={{ color: 'var(--m2-ink)' }}>
                      {selectedRolePlay ? displayRolePlayTitle(selectedRolePlay.title) : 'Select a role play'}
                    </div>
                    {/* <div className="text-[12.5px]" style={{ color: 'var(--m2-muted)' }}>
                      {selectedRolePlay ? `Sessions ${selectedRolePlay.sessionsPlayed}/${selectedRolePlay.targetSessions || '∞'}` : moduleConfig.name}
                    </div> */}
                  </div>
                </div>
                {/* <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-md" style={{ background: 'var(--m2-lime)', color: 'var(--m2-brand-dark)' }}>
                    <Clock size={13} />~{Math.max(1, Math.round(moduleConfig.timerDuration / 60))} min
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md capitalize" style={{ background: 'var(--m2-mint)', color: 'var(--m2-brand)' }}>
                    {selectedMode === 'voice' ? <Mic size={13} /> : <Keyboard size={13} />}
                    {selectedMode}
                  </div>
                </div> */}
              </div>
            </div>

            {/* Mobile/tablet: toggle cards panel */}
            <div className="lg:hidden flex items-center justify-between gap-2 px-2 py-1.5 shrink-0 border-b border-gray-100 bg-card/50">
              <button
                type="button"
                onClick={() => {
                  if (isGeneratingReview) return
                  setIsCardsPanelOpen(true)
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 active:bg-gray-200 touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Open Role Play cards"
                disabled={isGeneratingReview}
              >
                <LayoutList className="h-5 w-5 text-gray-500" />
                <span className="truncate">{selectedRolePlay ? displayRolePlayTitle(selectedRolePlay.title) : 'Role Play cards'}</span>
              </button>
            </div>

            {/* Mobile/tablet: cards drawer overlay */}
            {isCardsPanelOpen && (
              <div className="lg:hidden fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
                <button type="button" onClick={() => setIsCardsPanelOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity" aria-label="Close panel" />
                <div className="relative w-full max-w-[min(20rem,85vw)] h-full bg-card text-card-foreground border-r shadow-xl flex flex-col animate-in slide-in-from-left duration-200">
                  <div className="flex items-center justify-between p-3 border-b shrink-0">
                    <span className="text-sm font-medium text-gray-700">Role Play cards</span>
                    <button type="button" onClick={() => setIsCardsPanelOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 touch-manipulation" aria-label="Close">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-hidden flex flex-col min-h-0">{cardsListContent}</div>
                </div>
              </div>
            )}

            <Card className="relative flex flex-col flex-1 min-h-0 rounded-none border-0 shadow-none bg-transparent py-2 gap-2">
              <CardContent className="flex-1 overflow-y-auto p-3 sm:p-4 flex flex-col space-y-4 min-h-0">
                {messages.filter((msg) => msg.role !== 'system').length === 0 && coachReady && !isLoading ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <p>Send a message to start chatting</p>
                  </div>
                ) : (
                  messages
                    .filter((msg) => msg.role !== 'system')
                    .map((msg) => {
                      const role = msg.role === 'ai' ? 'assistant' : msg.role
                      const content = msg.content || ''
                      const coachPayload = role === 'assistant' && !msg.payload?.isReview && !msg.payload?.isReviewWarning ? parseCoachMessage(content) : null
                      return (
                        <div key={msg.id}>
                          {coachPayload ? (
                            <CoachMessageCard payload={coachPayload} />
                          ) : msg.payload?.isReview ? (
                            <div className="flex justify-start mb-4">
                              <div className="max-w-[85%] sm:max-w-[75%] rounded-lg bg-white border border-gray-200 shadow-sm px-4 py-3">
                                <ReviewContent
                                  mode={msg.payload.mode}
                                  score={msg.payload.score}
                                  createdAt={new Date(msg.timestamp).toISOString()}
                                  mastered={msg.payload.mastered}
                                  toImprove={msg.payload.toImprove}
                                  feedback={content}
                                />
                              </div>
                            </div>
                          ) : (
                            <MessageBubble role={role} content={content} isReviewWarning={Boolean(msg.payload?.isReviewWarning)} />
                          )}
                          <div className={`text-xs text-gray-500 mt-1 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>{new Date(msg.timestamp).toLocaleTimeString()}</div>
                        </div>
                      )
                    })
                )}
                {(isLoading || !coachReady) &&
                  (messages.some((m) => (m.role === 'ai' || m.role === 'assistant') && parseCoachMessage(m.content)) ? (
                    // A coach welcome already exists → this is a reply being generated: show the typing bubble.
                    <div className="self-start max-w-[80%]">
                      <div className="bg-secondary text-gray-800 px-3 py-2 rounded-lg inline-flex items-center gap-2">
                        <span className="inline-flex h-2 w-2">
                          <span className="h-2 w-2 rounded-full bg-primary animate-ping" />
                        </span>
                        <span className="text-sm font-semibold text-gray-800 tracking-wide ">Thinking...</span>
                      </div>
                    </div>
                  ) : (
                    // The welcome itself is still loading → show the skeleton shaped like the welcome card.
                    <CoachWelcomeSkeleton />
                  ))}
                <div ref={messagesEndRef} />
              </CardContent>

              {/* Action bar — mode toggle + start roleplay (template ".action-bar") */}
              <div className="flex items-center justify-between gap-3 mx-3 sm:mx-4 mt-1 px-3.5 py-2.5 rounded-xl border bg-white shrink-0" style={{ borderColor: 'var(--m2-line)' }}>
                {/* Text / Voice / Video pill toggle — template ".mode-toggle" */}
                <div className="inline-flex p-[3px] gap-0.5 rounded-full" style={{ background: 'var(--m2-mint-soft)' }}>
                  {(['text', 'voice', 'camera'] as const).map((m) => {
                    const active = selectedMode === m
                    return (
                      <button
                        key={m}
                        type="button"
                        disabled={isGeneratingReview}
                        onClick={() => setSelectedMode(m)}
                        className="flex items-center gap-1.5 px-[13px] py-1.5 rounded-full text-xs font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        style={active ? { background: '#fff', color: 'var(--m2-ink)', boxShadow: '0 1px 3px rgba(15,76,58,0.1)' } : { background: 'transparent', color: 'var(--m2-muted)' }}
                      >
                        {m === 'text' ? <Keyboard size={13} /> : m === 'voice' ? <Mic size={13} /> : <Video size={13} />}
                        {m === 'text' ? 'Text' : m === 'voice' ? 'Voice' : 'Video'}
                      </button>
                    )
                  })}
                </div>

                {/* Estimate + start (template ".action-right") */}
                <div className="flex items-center gap-2.5 shrink-0">
                  <div className="hidden sm:flex items-center gap-1 text-[11.5px]" style={{ color: 'var(--m2-muted)' }}>
                    <Clock size={12} />~{Math.max(1, Math.round(moduleConfig.timerDuration / 60))} min
                  </div>
                  <Button
                    className="shrink-0 h-auto inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.4px] hover:bg-[var(--m2-lime-hover)] disabled:opacity-60"
                    style={{ background: 'var(--m2-lime)', color: 'var(--m2-brand-dark)' }}
                    onClick={() => {
                      if (isGeneratingReview) return
                      // Under a minute left on a metered module: the server would refuse
                      // the start, so show the offer instead of opening the call.
                      if (module2Store.activeModuleIsOutOfMinutes) {
                        meteringStore.openOffer()
                        return
                      }
                      setIsSidebarOpen(true)
                    }}
                    disabled={!module2Store.selectedRolePlay || isGeneratingReview || isLoading}
                  >
                    <Play size={13} />
                    Start roleplay
                  </Button>
                </div>
              </div>

              {/* Message Input — chat dock (template ".chat-dock" / ".chat-input-row") */}
              <div className="px-3 sm:px-4 pt-2 pb-3 shrink-0">
                <div className="flex items-center gap-1.5 pl-1 mb-[7px] text-[11px] font-medium" style={{ color: 'var(--m2-muted)' }}>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: 'var(--m2-lime-hover)' }} aria-hidden="true" />
                  <span>
                    Need help?{' '}
                    <strong className="font-bold" style={{ color: 'var(--m2-brand)' }}>
                      Chat with Georgia
                    </strong>{' '}
                    &mdash; ask anything about this role play
                  </span>
                </div>
                <form
                  ref={formRef}
                  onSubmit={handleSendMessage}
                  className="flex items-center gap-2 rounded-full border-[1.5px] border-[color:var(--m2-mint-deep)] pl-2 pr-1 py-1 transition-all focus-within:border-[color:var(--m2-brand)] focus-within:shadow-[0_0_0_3px_rgba(197,242,106,0.22)]"
                  style={{ background: 'var(--m2-card)' }}
                >
                  <div
                    className="w-[30px] h-[30px] rounded-full overflow-hidden shrink-0 border-2 border-white"
                    style={{ background: 'linear-gradient(135deg, #0F4C3A 0%, #073529 100%)' }}
                    aria-hidden="true"
                  >
                    <img src={georgiaAvatar} alt="" className="w-full h-full object-cover" />
                  </div>
                  <ChatTextarea
                    value={message}
                    onValueChange={setMessage}
                    onEnterPress={() => {
                      if (!message.trim() || isLoading || !coachReady) return
                      formRef.current?.requestSubmit()
                    }}
                    placeholder="Ask Georgia anything about this role play…"
                    disabled={!coachReady}
                    rows={1}
                    autoResize={false}
                    className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none resize-none px-1 py-2 text-[13px] placeholder:text-[#8FA095] focus-visible:ring-0 focus-visible:border-0"
                    style={{ color: 'var(--m2-ink)' }}
                  />
                  <ParametersSummaryButton
                    parameters={selectedPayloadContent ? { context: selectedPayloadContent } : undefined}
                    disabled={isLoading || !coachReady}
                    className="h-8 w-8 rounded-full bg-transparent text-[var(--m2-muted)] hover:bg-[var(--m2-mint)] hover:text-[var(--m2-brand)]"
                  />
                  <Button
                    type="submit"
                    disabled={!message.trim() || isLoading || !coachReady}
                    className="h-8 w-8 shrink-0 p-0 rounded-full touch-manipulation hover:bg-[var(--m2-lime-hover)] disabled:opacity-60"
                    style={{ background: 'var(--m2-lime)', color: 'var(--m2-brand-dark)' }}
                  >
                    <SendHorizonal className="h-[15px] w-[15px]" />
                  </Button>
                </form>
              </div>
            </Card>
          </div>

          {/* Right rail: progress + last attempt for the selected role play (lg+ only) */}
          <Module2ProgressRail
            data={
              selectedRolePlay
                ? buildM2RailData(
                    selectedRolePlay,
                    reviews.filter((r) => r.rolePlayId === selectedRolePlay.id),
                  )
                : { sessions: { played: 0, target: 0 }, bestScore: 0, masteryPct: 0, lastAttempt: null }
            }
          />
        </div>

        {/* RolePlay Sidebar */}
        <RolePlaySidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          card={sidebarCard}
          objectives={coachObjectives}
          mode={selectedMode}
          flowId={moduleConfig.roleplayFlowId}
          timerDuration={moduleConfig.timerDuration}
          onStartRolePlay={() => {
            // Sidebar handles all modes itself
          }}
          onRolePlayEnd={handleRolePlayEnd}
          onSwitchMode={(mode) => {
            setIsSidebarOpen(false)
            setSelectedMode(mode)
            setReopenSidebarPending(true)
          }}
        />
      </>
    )
  }

  // Voice mode
  if (module2Store.mode === 'voice') {
    return (
      <MainScreen
        isRolePlay={true}
        footer={
          <div className="p-3 sm:p-4 border-t border-gray-100 bg-gradient-to-t from-gray-50 to-white flex justify-center items-center gap-3">
            <Button variant="outline" onClick={handleReturnToSelection} className="font-semibold px-4 sm:px-6 min-h-11 touch-manipulation w-full sm:w-auto max-w-xs">
              End Call
            </Button>
          </div>
        }
      />
    )
  }

  return null
}

export default observer(Module2Chat)
